// Lab Uji AI (khusus Preview, Owner). Backend menolak (404/403) bila flag mati atau bukan Owner.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FlaskConical, Upload, ShieldCheck, Ban, RefreshCw, Trash2, Copy } from "lucide-react";
import { SAMPLER_SOURCE, samplerHref } from "./sampler_source";
import { QtyVerifyPanel } from "./qty_verify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai-eval-lab`;
const ax = (cfg) => axios({ withCredentials: true, ...cfg });
const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : (e?.message || "Gagal"); };
const STATUS = { menunggu_pembersihan: ["Dibersihkan…", "#64748b"], menunggu_verifikasi: ["Menunggu verifikasi", "#b45309"],
  disetujui: ["Disetujui", "#15803d"], dikeluarkan: ["Dikeluarkan", "#b91c1c"] };

function useBlobUrl(url, bump) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let u = "", alive = true;
    if (!url) { setSrc(""); return undefined; }
    ax({ url, responseType: "blob" }).then(r => { if (alive) { u = URL.createObjectURL(r.data); setSrc(u); } }).catch(() => alive && setSrc(""));
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [url, bump]);
  return src;
}

function BookmarkletBox() {
  const [show, setShow] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(samplerHref()); alert("Kode bookmark tersalin."); } catch { alert("Gagal menyalin."); } };
  return <div className="card" style={{ padding: 12 }} data-testid="lab-bookmarklet">
    <b>1. Ambil sampel di LIVE (Owner, sekali klik)</b>
    <ol style={{ fontSize: 13, margin: "6px 0 6px 18px" }}>
      <li>Seret tombol di bawah ke bilah bookmark (atau buat bookmark baru lalu tempel kode dari "Salin kode").</li>
      <li>Buka aplikasi LIVE, login Owner seperti biasa, klik bookmark itu.</li>
      <li>Periksa daftar 10 sampel → <b>Setujui &amp; Unduh ZIP</b>. Tidak ada data LIVE yang diubah.</li>
    </ol>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {/* eslint-disable-next-line no-script-url */}
      <a href={samplerHref()} onClick={e => e.preventDefault()} className="primary small-btn" data-testid="lab-bookmarklet-link">Ambil Sampel Uji AI</a>
      <button className="soft-btn small-btn" onClick={copy} data-testid="lab-bookmarklet-copy"><Copy size={13} /> Salin kode</button>
      <button className="soft-btn small-btn" onClick={() => setShow(s => !s)} data-testid="lab-bookmarklet-audit">{show ? "Tutup" : "Lihat"} kode (audit)</button>
    </div>
    {show && <pre data-testid="lab-bookmarklet-source" style={{ maxHeight: 280, overflow: "auto", fontSize: 11, background: "#0f172a", color: "#e2e8f0", padding: 8, borderRadius: 6 }}>{SAMPLER_SOURCE}</pre>}
  </div>;
}

function UploadBox({ hasBatch, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 60 * 1024 * 1024) { setErr("ZIP melebihi 60 MB"); return; }
    if (hasBatch && !window.confirm("Batch lama akan dihapus dan diganti. Lanjutkan?")) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", f);
      await ax({ method: "post", url: `${API}/upload${hasBatch ? "?replace=1" : ""}`, data: fd });
      onDone();
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return <div className="card" style={{ padding: 12 }} data-testid="lab-upload">
    <b>2. Unggah ZIP hasil bookmark</b>
    <div className="muted small">Hanya ZIP dari bookmark TAMFARIS (maks. 10 gambar PNG/JPG/WebP/PDF, 10 MB per file). Disimpan di luar repository (folder terbatas).</div>
    <input ref={ref} type="file" accept=".zip,application/zip" hidden onChange={pick} data-testid="lab-upload-input" />
    <button className="primary small-btn" style={{ marginTop: 6 }} disabled={busy} onClick={() => ref.current?.click()} data-testid="lab-upload-btn">
      <Upload size={13} /> {busy ? "Mengunggah…" : "Pilih ZIP"}</button>
    {err && <div className="warn-box small" data-testid="lab-upload-error">{err}</div>}
  </div>;
}

function MaskTool({ src, onSave, busy }) {
  const [boxes, setBoxes] = useState([]);
  const [drag, setDrag] = useState(null);
  const rel = (e) => { const r = e.currentTarget.getBoundingClientRect(); return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))]; };
  const up = (e) => {
    if (!drag) return;
    const [x, y] = rel(e); const b = [Math.min(drag[0], x), Math.min(drag[1], y), Math.max(drag[0], x), Math.max(drag[1], y)];
    if (b[2] - b[0] > 0.005 && b[3] - b[1] > 0.005) setBoxes(bs => [...bs, b]);
    setDrag(null);
  };
  return <div>
    <div style={{ position: "relative", userSelect: "none", cursor: "crosshair" }} onMouseDown={e => setDrag(rel(e))} onMouseUp={up} data-testid="lab-mask-area">
      <img src={src} alt="asli" draggable={false} style={{ width: "100%", display: "block" }} />
      {boxes.map((b, i) => <div key={i} style={{ position: "absolute", left: `${b[0] * 100}%`, top: `${b[1] * 100}%`, width: `${(b[2] - b[0]) * 100}%`, height: `${(b[3] - b[1]) * 100}%`, background: "rgba(128,128,128,.85)" }} />)}
    </div>
    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
      <span className="muted small">Seret pada gambar asli untuk menutup area. {boxes.length} kotak.</span>
      <button className="soft-btn small-btn" disabled={!boxes.length || busy} onClick={() => setBoxes([])}>Reset</button>
      <button className="soft-btn small-btn" disabled={!boxes.length || busy} onClick={() => onSave(boxes).then(() => setBoxes([]))} data-testid="lab-mask-save">Tutup area &amp; bersihkan ulang</button>
    </div>
  </div>;
}

function AnswerEditor({ s, vocab, onSaved }) {
  const [items, setItems] = useState(() => (s.answers?.items || []).map(i => ({ item_no: i.item_no, product: i.product || "", sleeve: i.sleeve || "", color: i.color || "", visible: i.visible !== false, order_text: i.order_text })));
  const [msg, setMsg] = useState("");
  const set = (n, k, v) => setItems(xs => xs.map(x => x.item_no === n ? { ...x, [k]: v } : x));
  const sel = (n, k, v, opts) => <select value={v} onChange={e => set(n, k, e.target.value)} data-testid={`lab-ans-${s.sample_id}-${n}-${k}`}>
    <option value="">— pilih —</option>
    {v && !opts.includes(v) && <option value={v} disabled>(kategori lama) {v} — pilih ulang</option>}
    {opts.map(o => <option key={o} value={o}>{o}</option>)}</select>;
  const save = async () => {
    try { await ax({ method: "put", url: `${API}/samples/${s.sample_id}/answers`, data: { items: items.map(({ order_text, ...i }) => i) } }); setMsg("Kunci jawaban diverifikasi."); onSaved(); }
    catch (x) { setMsg(errMsg(x)); }
  };
  return <div style={{ fontSize: 12 }} data-testid={`lab-answers-${s.sample_id}`}>
    <b>Kunci jawaban</b> {s.answers?.verified ? <span style={{ color: "#15803d" }}>✓ diverifikasi {s.answers.verified_by}</span> : <span style={{ color: "#b45309" }}>(draf dari data pesanan — periksa gambar)</span>}
    <div className="muted" data-testid={`lab-ans-legend-${s.sample_id}`}>PDH = kemeja dengan skoder di bahu · Workshirt = kemeja tanpa skoder · kemeja_lainnya = kemeja selain keduanya</div>
    {(s.needs_reverify || []).length > 0 && <div style={{ color: "#b45309" }} data-testid={`lab-ans-reverify-${s.sample_id}`}>
      Perlu verifikasi ulang: item {s.needs_reverify.join(", ")} memakai kategori lama — pilih PDH / Workshirt / kemeja_lainnya sesuai gambar.</div>}
    {items.map(i => <div key={i.item_no} style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center", marginTop: 4 }}>
      <span>#{i.item_no}</span>{sel(i.item_no, "product", i.product, vocab.product)}{sel(i.item_no, "sleeve", i.sleeve, vocab.sleeve)}{sel(i.item_no, "color", i.color, vocab.color)}
      <label><input type="checkbox" checked={i.visible} onChange={e => set(i.item_no, "visible", e.target.checked)} /> terlihat</label>
      {s.product_hints?.[i.item_no] && s.product_hints[i.item_no] !== i.product && <span style={{ color: "#1d4ed8" }} data-testid={`lab-ans-hint-${s.sample_id}-${i.item_no}`}>saran nama produk: {s.product_hints[i.item_no]}</span>}
      <span className="muted" title={i.order_text} style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.order_text}</span>
    </div>)}
    <button className="soft-btn small-btn" style={{ marginTop: 4 }} onClick={save} data-testid={`lab-ans-save-${s.sample_id}`}>Simpan &amp; tandai terverifikasi</button>
    {msg && <span style={{ marginLeft: 6 }}>{msg}</span>}
  </div>;
}

function SampleCard({ s, vocab, onChange }) {
  const [bump, setBump] = useState(0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const raw = useBlobUrl(s.raw_available ? `${API}/samples/${s.sample_id}/image?kind=raw` : "", bump);
  const clean = useBlobUrl(s.report ? `${API}/samples/${s.sample_id}/image?kind=clean` : "", bump);
  const act = async (path, data) => {
    setBusy(true); setErr("");
    try { await ax({ method: "post", url: `${API}/samples/${s.sample_id}/${path}`, data }); setBump(b => b + 1); onChange(); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  const [label, color] = STATUS[s.status] || [s.status, "#334155"];
  const r = s.report || {};
  const open = s.status === "menunggu_verifikasi";
  return <div className="card" style={{ padding: 10 }} data-testid={`lab-sample-${s.sample_id}`}>
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <b>{s.sample_id} · {s.category} · {s.channel}</b>
      <span data-testid={`lab-status-${s.sample_id}`} style={{ color, fontWeight: 700 }}>{label}</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
      <div><div className="muted small">ASLI (hanya di Lab; dihapus setelah diputuskan)</div>
        {raw ? (open ? <MaskTool src={raw} busy={busy} onSave={b => act("masks", { boxes: b })} /> : <img src={raw} alt="asli" style={{ width: "100%" }} />) : <div className="muted small">—</div>}</div>
      <div><div className="muted small">BERSIH (yang kelak dikirim ke AI)</div>
        {clean ? <img src={clean} alt="bersih" style={{ width: "100%" }} data-testid={`lab-clean-${s.sample_id}`} /> : <div className="muted small">—</div>}</div>
    </div>
    {s.report && <div style={{ fontSize: 12, marginTop: 6 }}>
      Ditutup: {[...new Set((r.redactions || []).map(x => x.kind))].join(", ") || "tidak ada"} · area {((r.redacted_area || 0) * 100).toFixed(1)}%
      {(r.flags || []).map((f, i) => <div key={i} style={{ color: "#b45309" }}>⚠ {f}</div>)}
    </div>}
    {s.excluded && <div style={{ color: "#b91c1c", fontSize: 12 }}>Dikeluarkan: {s.excluded.reason} ({s.excluded.by})</div>}
    <div style={{ fontSize: 12, marginTop: 4, color: "#334155" }}>Wajib cek mata: tidak ada no. HP, nama/akun pelanggan, nomor pesanan, alamat, atau barcode tersisa; bentuk lengan, warna kain & jenis produk tetap terlihat.</div>
    {s.status !== "dikeluarkan" && <AnswerEditor key={`${s.sample_id}-${s.answers?.verified}`} s={s} vocab={vocab} onSaved={onChange} />}
    {open && <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
      <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Alasan (wajib bila ada ⚠ atau untuk mengeluarkan)" style={{ flex: 1, minWidth: 220 }} data-testid={`lab-reason-${s.sample_id}`} />
      <button className="primary small-btn" disabled={busy} onClick={() => act("approve", { reason })} data-testid={`lab-approve-${s.sample_id}`}><ShieldCheck size={13} /> Setujui gambar bersih</button>
      <button className="soft-btn small-btn" disabled={busy} onClick={() => act("exclude", { reason })} data-testid={`lab-exclude-${s.sample_id}`}><Ban size={13} /> Keluarkan</button>
    </div>}
    {err && <div className="warn-box small" data-testid={`lab-error-${s.sample_id}`}>{err}</div>}
  </div>;
}

export default function AiEvalLab() {
  const [st, setSt] = useState({ loading: true, enabled: false, data: null, err: "" });
  const load = useCallback(async () => {
    try {
      const r = await ax({ url: `${API}/samples` });
      setSt({ loading: false, enabled: true, data: r.data, err: "" });
    } catch (e) {
      setSt({ loading: false, enabled: false, data: null, err: e?.response?.status === 404 ? "Lab Uji AI tidak aktif di lingkungan ini." : errMsg(e) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const processing = st.data?.process?.state === "processing";
  useEffect(() => { if (!processing) return undefined; const t = setInterval(load, 2000); return () => clearInterval(t); }, [processing, load]);
  if (st.loading) return <div className="muted" style={{ padding: 16 }}>Memuat Lab…</div>;
  if (!st.enabled) return <div className="warn-box" style={{ margin: 16 }} data-testid="lab-disabled">{st.err}</div>;
  const list = st.data.samples || [];
  const count = (k) => list.filter(x => x.status === k).length;
  const purge = async () => { if (!window.confirm("Hapus semua sampel & hasil di Lab?")) return; await ax({ method: "delete", url: `${API}/batch` }); load(); };
  return <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }} data-testid="ai-eval-lab">
    <h2 style={{ margin: 0, display: "flex", gap: 8, alignItems: "center" }}><FlaskConical size={20} /> Lab Uji AI Visual <span className="muted small">(Preview · Owner)</span></h2>
    <div className="muted small" data-testid="lab-no-ai">Tahap verifikasi saja — Lab ini tidak mengirim gambar ke AI dan tidak memakai biaya API.</div>
    <QtyVerifyPanel />
    <BookmarkletBox />
    <UploadBox hasBatch={list.length > 0} onDone={load} />
    {list.length > 0 && <div className="card" style={{ padding: 10, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid="lab-summary">
      <b>3. Verifikasi ({list.length} sampel)</b>
      <span>Disetujui {count("disetujui")}</span><span>Dikeluarkan {count("dikeluarkan")}</span><span>Menunggu {count("menunggu_verifikasi") + count("menunggu_pembersihan")}</span>
      {processing && <span data-testid="lab-processing">Membersihkan {st.data.process.done}/{st.data.process.total}…</span>}
      <button className="soft-btn small-btn" onClick={load}><RefreshCw size={13} /> Muat ulang</button>
      <button className="soft-btn small-btn" onClick={purge} data-testid="lab-purge"><Trash2 size={13} /> Hapus batch</button>
    </div>}
    {list.map(s => <SampleCard key={s.sample_id} s={s} vocab={st.data.vocab} onChange={load} />)}
  </div>;
}
